import type { EvidenceLevel, ModuleId, ModuleSummary, ProtocolDefinition, SessionMode } from './types';

/**
 * 7.4.0 shortens Landolt C exposure to 1/5 at maximum difficulty and speeds
 * every Visual Sweeps level by 1.5×. 7.3.0 changed sign size and target speed.
 */
export const PROTOCOL_VERSION = '7.4.0';
export const TRAINING_BLOCK_SIZE = 8;
export const MINIMUM_VIEWPORT = { width: 1280, height: 720 } as const;
export const MINIMUM_REACTION_TIME_MS = 150;
/** A fixed 24-trial form is considered interpretable only with at least 75% valid trials. */
export const MINIMUM_VALID_ASSESSMENT_TRIALS = 18;

export const MODULE_EVIDENCE: Readonly<Record<ModuleId, EvidenceLevel>> = {
  'read-switch': 'trained-task',
  'central-peripheral': 'component-skill',
  'player-track': 'component-skill',
  'moving-target': 'measurement-only',
};

export const MODULE_PARAMETERS: Readonly<Record<ModuleId, Readonly<Record<string, number | string | boolean>>>> = {
  'read-switch': {
    paradigm: 'two-successive-visual-sweeps',
    referenceProduct: 'BrainHQ Visual Sweeps',
    responseStages: 2,
    orientations: 4,
    spatialFrequencyControl: 'continuous-with-four-fixed-assessment-levels',
    stimulusWindow: 'localized-gaussian-envelope',
    edgeRendering: 'sinusoidal-gabor-wave-on-dotted-field',
    contrastRange: '0.11-0.18',
    sweepDurationMsRange: '120-347',
    speedMultiplierFromProtocol7_3: 1.5,
    interSweepMsRange: '60-120',
    backgroundPalettes: 4,
    adaptiveVariable: 'sweepDurationMs,spacingChangeRatio,contrast',
    adaptiveStrategy: 'rapid-placement-then-75-percent-convergence',
    primaryInput: 'pointer-buttons',
  },
  'central-peripheral': {
    paradigm: 'two-stage-central-peripheral-decision',
    referenceProduct: 'BrainHQ Double Decision',
    eccentricityMinimumDegrees: 4.5,
    eccentricityMaximumDegrees: 8,
    radialLocations: 8,
    centralAlternatives: 2,
    centralStimuli: 'same-color-generated-vehicle-illustration-pairs',
    background: 'adaptive-procedural-road-scenes',
    peripheralStimuli: 'one-black-circle-among-black-crosses-on-white-signs',
    centralVehicleScaleFromProtocol5: 2,
    peripheralSignScaleFromProtocol5: 1,
    maskMs: 260,
    presentationMsRange: '363-725',
    adaptiveLevelChanges: 'performance-target-duration,eccentricity,vehicle-similarity,background,distractors',
    adaptiveStrategy: 'rapid-placement-then-75-percent-convergence',
    primaryInput: 'pointer-vehicle-then-location',
  },
  'player-track': {
    objects: 8,
    targets: 4,
    referenceProduct: 'NeuroTracker / BrainHQ Target Tracker',
    cueMs: 1200,
    distractorFadeMs: 800,
    trackingMs: 5000,
    assessmentSpeedsDegreesPerSecond: '7.4,9.9,12.4',
    staircase: 'rapid-placement-then-75-percent-convergence',
    primaryInput: 'pointer-object-selection',
  },
  'moving-target': {
    speedDegreesPerSecondRange: '24-40',
    displayDurationMsRange: '280-1400',
    referenceProduct: 'DinVA / sports-vision dynamic-acuity systems',
    choices: 4,
    movement: 'seeded-random-walk-from-four-edges',
    sizeScale: 'logarithmic',
    adaptiveStrategy: 'rapid-placement-then-75-percent-convergence',
    primaryInput: 'pointer-direction-buttons',
  },
};

export const SESSION_TRIALS: Readonly<Record<SessionMode, number>> = {
  familiarization: 2,
  'baseline-a': 24,
  'baseline-b': 24,
  training: 24,
  reassessment: 24,
  retention: 24,
};

export const MODULE_IDS = Object.keys(MODULE_EVIDENCE) as ModuleId[];
const modes: SessionMode[] = [
  'familiarization',
  'baseline-a',
  'baseline-b',
  'training',
  'reassessment',
  'retention',
];

function isFixedDifficulty(mode: SessionMode): boolean {
  return mode !== 'training';
}

export const PROTOCOL_DEFINITIONS: readonly ProtocolDefinition[] = modes.flatMap((mode) =>
  MODULE_IDS.map((moduleId) => ({
    id: `${moduleId}:${mode}:${PROTOCOL_VERSION}`,
    version: PROTOCOL_VERSION,
    moduleId,
    mode,
    evidenceLevel: MODULE_EVIDENCE[moduleId],
    fixedDifficulty: isFixedDifficulty(mode),
    parameters: {
      ...MODULE_PARAMETERS[moduleId],
      trialCount: SESSION_TRIALS[mode],
      blockSize: TRAINING_BLOCK_SIZE,
      feedback: mode === 'training' || mode === 'familiarization',
    },
  })),
);

export function getProtocol(moduleId: ModuleId, mode: SessionMode): ProtocolDefinition {
  const protocol = PROTOCOL_DEFINITIONS.find(
    (candidate) => candidate.moduleId === moduleId && candidate.mode === mode,
  );
  if (!protocol) throw new Error(`Unknown protocol: ${moduleId}/${mode}`);
  return protocol;
}

export function viewportMeetsMinimum(width: number, height: number): boolean {
  return width >= MINIMUM_VIEWPORT.width && height >= MINIMUM_VIEWPORT.height;
}

export function assessmentSummariesAreComplete(
  summaries: readonly ModuleSummary[],
): boolean {
  if (summaries.length !== MODULE_IDS.length) return false;
  const byModule = new Map(summaries.map((summary) => [summary.moduleId, summary]));
  return byModule.size === MODULE_IDS.length && MODULE_IDS.every(
    (moduleId) => (byModule.get(moduleId)?.validTrials ?? 0) >= MINIMUM_VALID_ASSESSMENT_TRIALS,
  );
}
